import React from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'

const USERNAME_RE = /^[a-zA-Z0-9._-]+$/
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function isUserActive(active) {
  return active === true || active === 1 || active === '1' || active === 't'
}

function normalizeUserRow(u) {
  if (!u || typeof u !== 'object') return u
  return { ...u, active: isUserActive(u.active) }
}

function mapServerError(message) {
  const m = (message || '').toLowerCase()
  const fieldErrors = {}
  let formError = message || 'Failed to save user'

  if (m.includes('username') && m.includes('name')) {
    fieldErrors.username = 'Username is required.'
    fieldErrors.name = 'Full name is required.'
    formError = null
  } else if (m.includes('username must be 3-64')) {
    fieldErrors.username = 'Username must be 3–64 characters.'
    formError = null
  } else if (m.includes('username may only contain')) {
    fieldErrors.username = 'Use only letters, numbers, dots, hyphens, and underscores.'
    formError = null
  } else if (m.includes('username exists') || m.includes('duplicate')) {
    fieldErrors.username = 'This username is already in use.'
    formError = null
  } else if (m.includes('invalid email')) {
    fieldErrors.email = 'Enter a valid email address.'
    formError = null
  } else if (m.includes('email is required')) {
    fieldErrors.email = 'Email is required.'
    formError = null
  } else if (m.includes('send_welcome_email')) {
    fieldErrors.email = 'Email is required to send a welcome email.'
    formError = null
  } else if (m.includes('password must be at least 8') || m.includes('new_password')) {
    fieldErrors.password = 'Password must be at least 8 characters.'
    formError = null
  } else if (m.includes('password is required')) {
    fieldErrors.password = 'Password is required.'
    formError = null
  } else if (m.includes('already linked')) {
    fieldErrors.employeeId = message || 'That employee is already linked to another user.'
    formError = null
  } else if (m === 'invalid request') {
    formError = 'Request was rejected. Check the highlighted fields and try again.'
  }

  return { fieldErrors, formError }
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-1">
        {label}
        {required && <span className="text-red-600 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}

function inputClass(hasError) {
  return `input-field w-full${hasError ? ' border-red-500 ring-1 ring-red-500 focus:ring-red-500' : ''}`
}

export default function UsersAdmin() {
  const { user, hasRole } = useAuth()
  const [items, setItems] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [open, setOpen] = React.useState(false)
  const [editing, setEditing] = React.useState(null)
  const [form, setForm] = React.useState({ username:'', name:'', role:'viewer', email:'', phone:'', active:true, password:'', sendWelcomeEmail:false, linkEmployee: false, employeeId: null })
  const [roles, setRoles] = React.useState([])
  const [linkableEmployees, setLinkableEmployees] = React.useState([])

  const selectedEmployee = React.useMemo(
    () => linkableEmployees.find((e) => String(e.staff_id) === String(form.employeeId)),
    [linkableEmployees, form.employeeId]
  )
  const employeeRecordEmail = (selectedEmployee?.email || '').trim()
  const emailLockedFromEmployee = !editing && form.linkEmployee && form.employeeId && !!employeeRecordEmail
  const [fieldErrors, setFieldErrors] = React.useState({})
  const [formError, setFormError] = React.useState(null)
  const [touched, setTouched] = React.useState(false)
  const [resending, setResending] = React.useState(null)
  const [blocking, setBlocking] = React.useState(null)

  const canPermanentDelete = hasRole('org_admin') || hasRole('super_admin')

  const loadLinkableEmployees = React.useCallback(async (forUsername = null) => {
    const qs = forUsername ? `?for_username=${encodeURIComponent(forUsername)}` : ''
    const er = await authFetch(getLocalApiUrl(`/api/admin/users/linkable-employees${qs}`)).then(r => r.json()).catch(() => ({ success: true, data: [] }))
    setLinkableEmployees(er.data || [])
    return er.data || []
  }, [])

  const load = React.useCallback(async()=>{
    try {
      setLoading(true); setError(null)
      const [ur, rr] = await Promise.all([
        authFetch(getLocalApiUrl('/api/admin/users')).then(r=>r.json()),
        authFetch(getLocalApiUrl('/api/admin/roles')).then(r=>r.json()),
      ])
      if (!ur.success) throw new Error(ur.error || 'Failed to load users')
      if (!rr.success) throw new Error(rr.error || 'Failed to load roles')
      setItems((ur.data || []).map(normalizeUserRow))
      setRoles(rr.data || [])
      await loadLinkableEmployees()
    } catch(e){ setError(e.message) } finally { setLoading(false) }
  }, [loadLinkableEmployees])

  React.useEffect(()=>{ load() }, [load])

  const clearValidation = () => {
    setFieldErrors({})
    setFormError(null)
    setTouched(false)
  }

  const startCreate = async () => {
    setEditing(null)
    clearValidation()
    setForm({ username:'', name:'', role: (roles[0]?.role) || 'viewer', email:'', phone:'', active:true, password:'', sendWelcomeEmail:false, linkEmployee: false, employeeId: null })
    await loadLinkableEmployees()
    setOpen(true)
  }

  const startEdit = async (u) => {
    setEditing(u)
    clearValidation()
    setForm({ username:u.username, name:u.name, role:u.role, email:u.email||'', phone:u.phone||'', active:!!u.active, password:'', sendWelcomeEmail:false, linkEmployee: !!(u.employee_id || u.is_employee), employeeId: u.employee_id || null })
    await loadLinkableEmployees(u.username)
    setOpen(true)
  }

  const patchForm = (patch) => {
    setForm((p) => ({ ...p, ...patch }))
    const keys = Object.keys(patch)
    setFieldErrors((prev) => {
      const next = { ...prev }
      keys.forEach((k) => { delete next[k] })
      return next
    })
    setFormError(null)
  }

  const validateForm = () => {
    const trim = (v) => (typeof v === 'string' ? v.trim() : v)
    const username = trim(form.username)
    const name = trim(form.name)
    const email = trim(form.email)
    const password = trim(form.password)
    const next = {}

    if (!editing) {
      if (!username) next.username = 'Username is required.'
      else if (username.length < 3 || username.length > 64) next.username = 'Username must be 3–64 characters.'
      else if (!USERNAME_RE.test(username)) next.username = 'Use only letters, numbers, dots, hyphens, and underscores.'
    }

    if (!name) next.name = 'Full name is required.'

    if (!email) next.email = 'Email is required.'
    else if (!EMAIL_RE.test(email)) next.email = 'Enter a valid email address.'

    if (!editing) {
      if (!password && !form.sendWelcomeEmail) next.password = 'Enter a password or enable welcome email to send a setup link.'
      else if (password && password.length < 8) next.password = 'Password must be at least 8 characters.'
    } else if (password && password.length < 8) {
      next.password = 'Password must be at least 8 characters.'
    }

    return next
  }

  const buildPayload = () => {
    const trim = (v) => (typeof v === 'string' ? v.trim() : v)
    const name = trim(form.name)
    const email = trim(form.email)
    const phone = trim(form.phone)
    const password = trim(form.password)
    if (editing) {
      const base = { name, email, role: form.role, active: form.active, phone: phone || null }
      base.employee_id = form.linkEmployee && form.employeeId ? form.employeeId : null
      return base
    }
    const base = { username: trim(form.username), name, email, role: form.role, active: form.active }
    if (phone) base.phone = phone
    if (password) base.password = password
    if (form.sendWelcomeEmail) base.send_welcome_email = true
    if (form.linkEmployee && form.employeeId) base.employee_id = form.employeeId
    return base
  }

  const handleEmployeeSelect = (employeeId) => {
    if (!employeeId) {
      patchForm({ employeeId: null, name: '', email: '' })
      return
    }
    const emp = linkableEmployees.find(e => String(e.staff_id) === String(employeeId))
    if (emp) {
      patchForm({
        employeeId: emp.staff_id,
        name: emp.name || emp.full_name || '',
        email: emp.email || ''
      })
    }
  }

  const save = async() => {
    setTouched(true)
    const clientErrors = validateForm()
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors)
      setFormError('Please fix the errors below.')
      return
    }

    setFieldErrors({})
    setFormError(null)

    try {
      setLoading(true)
      setError(null)
      const method = editing ? 'PUT' : 'POST'
      const url = editing
        ? `/api/admin/users/${encodeURIComponent(editing.username)}`
        : '/api/admin/users'
      const payload = buildPayload()
      const r = await authFetch(getLocalApiUrl(url), {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const j = await r.json()
      if (!r.ok || !j.success) {
        const mapped = mapServerError(j.error)
        setFieldErrors(mapped.fieldErrors)
        setFormError(mapped.formError)
        return
      }

      let welcomeEmailWarning = null
      if (!editing && form.sendWelcomeEmail) {
        const we = j.data?.welcomeEmail
        if (we && !we.skipped && !we.success) {
          welcomeEmailWarning = `User created, but welcome email failed: ${we.error || 'unknown error'}`
        }
      }

      if (editing && form.password && form.password.trim()) {
        const pr = await authFetch(
          getLocalApiUrl(`/api/admin/users/${encodeURIComponent(editing.username)}/password`),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ new_password: form.password.trim() }),
          }
        )
        const pj = await pr.json()
        if (!pr.ok || !pj.success) {
          const mapped = mapServerError(pj.error)
          setFieldErrors(mapped.fieldErrors)
          setFormError(mapped.formError || 'Failed to set password')
          return
        }
      }

      setOpen(false)
      setEditing(null)
      clearValidation()
      if (welcomeEmailWarning) setError(welcomeEmailWarning)
      await load()
    } catch (e) {
      setFormError(e.message || 'Failed to save user')
    } finally {
      setLoading(false)
    }
  }

  const blockUser = async (u) => {
    if (user?.username === u.username || user?.sub === u.username) {
      setError('You cannot block your own account.')
      return
    }
    if (!confirm(`Block user ${u.username}? They will not be able to sign in, but their record will be kept.`)) return
    setBlocking(u.username)
    setError(null)
    try {
      const r = await authFetch(
        getLocalApiUrl(`/api/admin/users/${encodeURIComponent(u.username)}/block`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
      )
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) throw new Error(j.error || 'Failed to block user')
      setItems((prev) => prev.map((item) => (item.username === u.username ? { ...item, active: false } : item)))
      await load()
    } catch (e) {
      setError(e.message || 'Failed to block user')
    } finally {
      setBlocking(null)
    }
  }

  const unblockUser = async (u) => {
    if (!confirm(`Unblock user ${u.username}? They will be able to sign in again.`)) return
    setBlocking(u.username)
    setError(null)
    try {
      const r = await authFetch(
        getLocalApiUrl(`/api/admin/users/${encodeURIComponent(u.username)}/unblock`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
      )
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) throw new Error(j.error || 'Failed to unblock user')
      setItems((prev) => prev.map((item) => (item.username === u.username ? { ...item, active: true } : item)))
      await load()
    } catch (e) {
      setError(e.message || 'Failed to unblock user')
    } finally {
      setBlocking(null)
    }
  }

  const permanentDelete = async (u) => {
    if (!canPermanentDelete) {
      setError('Only an organisation administrator can permanently delete users.')
      return
    }
    if (u.active) {
      setError('Block the user before attempting permanent delete.')
      return
    }
    setError(null)
    try {
      const check = await authFetch(
        getLocalApiUrl(`/api/admin/users/${encodeURIComponent(u.username)}/delete-eligibility`)
      )
      const cj = await check.json().catch(() => ({}))
      if (!check.ok || !cj.success) {
        throw new Error(cj.error || 'Unable to check whether this user can be deleted')
      }
      if (!cj.can_delete) {
        const reasons = (cj.blockers || []).map((b) => b.message).filter(Boolean).join('\n')
        window.alert(reasons || 'This user cannot be permanently deleted.')
        return
      }
    } catch (e) {
      setError(e.message || 'Unable to verify delete eligibility')
      return
    }
    if (
      !confirm(
        `Permanently delete user ${u.username}? This cannot be undone.\n\nOnly proceed if you are sure no audit history is required for this account.`
      )
    ) {
      return
    }
    setBlocking(u.username)
    try {
      const r = await authFetch(
        getLocalApiUrl(`/api/admin/users/${encodeURIComponent(u.username)}`),
        { method: 'DELETE' }
      )
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) {
        const reasons = (j.blockers || []).map((b) => b.message).filter(Boolean).join(' ')
        throw new Error(reasons || j.error || 'Failed to delete user')
      }
      await load()
    } catch (e) {
      setError(e.message || 'Failed to delete user')
    } finally {
      setBlocking(null)
    }
  }

  const resendCredentials = async (u) => {
    if (!u.email) {
      setError('This user has no email on file. Add an email in Edit before resending credentials.')
      return
    }
    if (!confirm(`Send a password setup link to ${u.email}?`)) return
    setResending(u.username)
    setError(null)
    try {
      const r = await authFetch(
        getLocalApiUrl(`/api/admin/users/${encodeURIComponent(u.username)}/resend-credentials`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
      )
      const j = await r.json()
      if (!r.ok || !j.success) {
        throw new Error(j.error || 'Failed to resend credentials')
      }
      setError(null)
      alert(`Password setup link emailed to ${u.email}`)
    } catch (e) {
      setError(e.message || 'Failed to resend credentials')
    } finally {
      setResending(null)
    }
  }

  const showErrors = touched

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">User Management</h1>
            <div className="text-xs text-gray-500">
              Create logins and assign a <strong>system role</strong> (API permissions). Job designation is set on the employee record in Employees.
            </div>
          </div>
          <div className="text-sm text-gray-500">{loading? 'Loading…' : ''}</div>
        </div>
        {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
        <div className="mt-3">
          <button className="btn-primary" onClick={startCreate}>Add User</button>
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">Username</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Employee</th>
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-left">System role</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {items.map(u => (
              <tr
                key={u.username}
                className={!u.active ? 'bg-amber-50/70' : undefined}
              >
                <td className={`px-3 py-2 font-mono${!u.active ? ' text-gray-500' : ''}`}>{u.username}</td>
                <td className={`px-3 py-2${!u.active ? ' text-gray-600' : ''}`}>{u.name}</td>
                <td className="px-3 py-2">
                  {u.is_employee || u.employee_id ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Employee
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      External
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {u.employee_name || u.employee_id ? (
                    <span className="text-gray-800">{u.employee_name || `Staff #${u.employee_id}`}</span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2">{u.email || '—'}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    u.role === 'admin' || u.role === 'super_admin' ? 'bg-red-100 text-red-800' :
                    u.role === 'viewer' ? 'bg-indigo-100 text-indigo-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>{u.role}</span>
                </td>
                <td className="px-3 py-2">
                  {u.active ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900 ring-1 ring-amber-200">
                      Blocked
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button
                    className="btn-secondary mr-2"
                    onClick={() => resendCredentials(u)}
                    disabled={!u.email || resending === u.username || blocking === u.username}
                    title={u.email ? 'Email a password setup link' : 'Email required'}
                  >
                    {resending === u.username ? 'Sending…' : 'Send setup link'}
                  </button>
                  <button className="btn-secondary mr-2" onClick={()=>startEdit(u)}>Edit</button>
                  {u.active ? (
                    <button
                      className="btn-secondary mr-2"
                      onClick={() => blockUser(u)}
                      disabled={blocking === u.username || user?.username === u.username || user?.sub === u.username}
                      title={user?.username === u.username ? 'Cannot block yourself' : 'Block sign-in for this user'}
                    >
                      {blocking === u.username ? 'Blocking…' : 'Block'}
                    </button>
                  ) : (
                    <>
                      <button
                        className="btn-secondary mr-2"
                        onClick={() => unblockUser(u)}
                        disabled={blocking === u.username}
                      >
                        {blocking === u.username ? 'Saving…' : 'Unblock'}
                      </button>
                      {canPermanentDelete && (
                        <button
                          className="btn-secondary text-red-700 border-red-200"
                          onClick={() => permanentDelete(u)}
                          disabled={blocking === u.username}
                          title="Permanently remove this blocked user (org admin only)"
                        >
                          Delete
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
            {items.length===0 && (
              <tr><td className="px-3 py-4 text-gray-500" colSpan={8}>No users</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4" onClick={()=>setOpen(false)}>
          <div className="bg-white rounded shadow w-full max-w-md" onClick={(e)=>e.stopPropagation()}>
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div>
                <div className="text-lg font-semibold">{editing ? 'Edit User' : 'Add User'}</div>
                {editing && !form.active && (
                  <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900 ring-1 ring-amber-200">
                    Blocked — cannot sign in
                  </div>
                )}
              </div>
              <button className="btn-secondary" onClick={()=>setOpen(false)}>Close</button>
            </div>

            {editing && !form.active && (
              <div className="mx-4 mt-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
                This account is <strong>blocked</strong>. The user cannot sign in until you unblock them from the list,
                or check <strong>Active</strong> below and save.
              </div>
            )}

            {showErrors && formError && (
              <div className="mx-4 mt-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {formError}
              </div>
            )}

            <div className="p-4 grid grid-cols-2 gap-3">
              <div className="col-span-2 mb-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    id="link-employee"
                    type="checkbox"
                    checked={form.linkEmployee}
                    onChange={(e) => {
                      const checked = e.target.checked
                      patchForm({ linkEmployee: checked, employeeId: checked ? form.employeeId : null })
                      if (!checked && !editing) patchForm({ name: '', email: '' })
                    }}
                    disabled={!editing && linkableEmployees.length === 0}
                  />
                  <label htmlFor="link-employee" className="text-sm font-medium text-gray-700">
                    Link to employee
                  </label>
                  {!editing && linkableEmployees.length === 0 && (
                    <span className="text-xs text-gray-400 ml-2">(no employees without accounts)</span>
                  )}
                </div>
                {form.linkEmployee && (
                  <div>
                    <select
                      className={`select-field w-full${showErrors && fieldErrors.employeeId ? ' border-red-500 ring-1 ring-red-500' : ''}`}
                      value={form.employeeId || ''}
                      onChange={(e) => handleEmployeeSelect(e.target.value)}
                    >
                      <option value="">-- Select employee --</option>
                      {linkableEmployees.map(emp => (
                        <option key={emp.staff_id} value={emp.staff_id}>
                          {emp.name || emp.full_name} {emp.staff_role ? `(${emp.staff_role})` : ''} {emp.email ? `— ${emp.email}` : ''}
                        </option>
                      ))}
                    </select>
                    {showErrors && fieldErrors.employeeId && (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.employeeId}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      {editing
                        ? 'Link this login to an employee record so they appear in investigation pipeline assignees. Uncheck to remove the link.'
                        : employeeRecordEmail
                          ? 'Name and email are taken from the employee record.'
                          : 'Name is taken from the employee record. Enter an email below — it will be saved on the employee and user account.'}
                    </p>
                  </div>
                )}
                {!form.linkEmployee && (
                  <p className="text-xs text-gray-500">
                    {editing
                      ? 'External users can still sign in but cannot be assigned investigation cases unless linked to an employee.'
                      : linkableEmployees.length > 0
                        ? 'Check this to create a user account for an existing employee. Leave unchecked for external users (auditors, consultants).'
                        : 'All employees already have user accounts, or no employees exist yet. Go to Employees to add staff first.'}
                  </p>
                )}
              </div>
              <div className="col-span-2">
                <Field label="Username" required={!editing} error={showErrors ? fieldErrors.username : null}>
                  <input
                    className={inputClass(showErrors && fieldErrors.username)}
                    value={form.username}
                    onChange={(e)=>patchForm({ username: e.target.value })}
                    disabled={!!editing}
                    autoComplete="off"
                  />
                </Field>
              </div>
              <div className="col-span-2">
                <Field label="Full Name" required error={showErrors ? fieldErrors.name : null}>
                  <input
                    className={inputClass(showErrors && fieldErrors.name)}
                    value={form.name}
                    onChange={(e)=>patchForm({ name: e.target.value })}
                    disabled={!editing && form.linkEmployee && form.employeeId}
                  />
                </Field>
              </div>
              <div>
                <Field label="Email" required error={showErrors ? fieldErrors.email : null}>
                  <input
                    type="email"
                    className={inputClass(showErrors && fieldErrors.email)}
                    value={form.email}
                    onChange={(e)=>patchForm({ email: e.target.value })}
                    disabled={emailLockedFromEmployee}
                    placeholder={form.linkEmployee && form.employeeId && !employeeRecordEmail ? 'Enter work email for this employee' : undefined}
                  />
                </Field>
              </div>
              <div>
                <Field label="Phone" error={showErrors ? fieldErrors.phone : null}>
                  <input
                    className={inputClass(showErrors && fieldErrors.phone)}
                    value={form.phone}
                    onChange={(e)=>patchForm({ phone: e.target.value })}
                    placeholder="Optional"
                  />
                </Field>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">System role</label>
                <select className="select-field w-full" value={form.role} onChange={(e)=>patchForm({ role: e.target.value })}>
                  {['system','group','venue',''].map(tier => {
                    const tierRoles = (roles||[]).filter(r => (r.tier || '') === tier)
                    if (tierRoles.length === 0) return null
                    return (
                      <optgroup key={tier || 'other'} label={(tier || 'Other').charAt(0).toUpperCase() + (tier || 'other').slice(1) + ' Tier'}>
                        {tierRoles.map(r => <option key={r.role} value={r.role}>{r.display_name || r.role}{r.is_system ? ' (system)' : ''}</option>)}
                      </optgroup>
                    )
                  })}
                </select>
                <p className="mt-1 text-xs text-gray-500">Controls API permissions (e.g. mobile UAR create vs manage). Not the employee job designation.</p>
              </div>
              <div className={`flex items-center gap-2 mt-6 rounded-md px-2 py-1.5 -mx-2 ${editing && !form.active ? 'bg-amber-50 ring-1 ring-amber-200' : ''}`}>
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e)=>patchForm({ active: e.target.checked })}
                  disabled={editing && (user?.username === editing.username || user?.sub === editing.username) && !form.active}
                />
                <span className="text-sm">
                  {form.active ? 'Active — can sign in' : 'Blocked — cannot sign in'}
                </span>
              </div>
              {!editing && (
                <div className="col-span-2">
                  <Field
                    label="Password"
                    required={!form.sendWelcomeEmail}
                    error={showErrors ? fieldErrors.password : null}
                  >
                    <input
                      type="password"
                      className={inputClass(showErrors && fieldErrors.password)}
                      value={form.password}
                      onChange={(e)=>patchForm({ password: e.target.value })}
                      placeholder={form.sendWelcomeEmail ? 'Optional — user sets password via email link' : 'At least 8 characters'}
                      autoComplete="new-password"
                    />
                  </Field>
                </div>
              )}
              {!editing && (
                <div className="col-span-2 flex items-start gap-2">
                  <input
                    id="send-welcome-email"
                    type="checkbox"
                    checked={form.sendWelcomeEmail}
                    onChange={(e)=>patchForm({ sendWelcomeEmail: e.target.checked })}
                  />
                  <label htmlFor="send-welcome-email" className="text-sm text-gray-700">
                    Send welcome email with password setup link
                  </label>
                </div>
              )}
              {editing && (
                <>
                  <div className="col-span-2">
                    <Field label="Set New Password" error={showErrors ? fieldErrors.password : null}>
                      <input
                        type="password"
                        className={inputClass(showErrors && fieldErrors.password)}
                        value={form.password}
                        onChange={(e)=>patchForm({ password: e.target.value })}
                        placeholder="Leave blank to keep current (no email)"
                        autoComplete="new-password"
                      />
                    </Field>
                    <p className="mt-1 text-xs text-gray-500">
                      Use <strong>Send setup link</strong> on the user list to email a password setup link.
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
              <button className="btn-secondary" onClick={()=>setOpen(false)}>Cancel</button>
              <button className="btn-primary" onClick={save} disabled={loading}>
                {loading ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
